// Ubicación en el repo: src/lib/appointments.js
// Queries de Agenda (spec-pacientes-agenda.md §2). El filtrado de
// seguridad real (quién puede ver/gestionar qué cita) lo hace RLS sobre
// `appointments` (has_branch_access(branch_id)) — acá solo se agregan
// filtros de UX (sucursal/módulo/día activos).
import { supabase } from './supabaseClient';
import { formatDateLong } from './format';

export const ORL_APPT_TYPES = [
  'Consulta',
  'Audiometría',
  'Control audífono',
  'Venta y calibración',
];
export const OFT_APPT_TYPES = ['Consulta', 'Receta de lentes', 'Venta'];
export const BLOCK_TYPES = ['Trabajo administrativo', 'Trabajo en terreno', 'Reunión'];
export const DURATIONS = [15, 30, 45, 60, 90, 120];
// Igual que STATUS_FLOW/STATUS_COLORS/STATUS_ICONS de prototipo-actual.html
// (líneas 1105-1108) — "No se presentó" SÍ es parte del flujo de estados,
// no un caso aparte.
export const STATUS_FLOW = ['Pendiente', 'Recepcionado', 'En atención', 'Atendido', 'No se presentó'];
export const STATUS_ALL = STATUS_FLOW;
export const STATUS_COLORS = {
  Pendiente: '#94A3B8',
  Recepcionado: '#4F46E5',
  'En atención': '#0891B2',
  Atendido: '#65A30D',
  'No se presentó': '#B91C1C',
};
export const STATUS_ICONS = {
  // Pendiente asignado 21-ago-2026 (pedido de Nicolás, DECISIONS.md #79) —
  // antes no tenía ícono propio, así que la leyenda le mostraba solo un
  // punto de color mientras los otros 4 estados tenían ícono. Desde el
  // mismo día, más tarde, también pasó a ser un botón clickeable más en
  // Agenda.jsx (STATUS_FLOW completo, ya no STATUS_FLOW.slice(1)) — sirve
  // para volver una cita a Pendiente si se marcó otro estado por error, y
  // es el estado inicial tanto de citas agendadas en sucursal como de
  // auto-agendamiento.
  Pendiente: '⏳',
  Recepcionado: '📥',
  'En atención': '🩺',
  Atendido: '✅',
  'No se presentó': '🚫',
};
// Igual que TYPE_COLORS de prototipo-actual.html (línea 1098) — color por
// tipo de atención, usado en el borde de cada cita y en la leyenda.
export const TYPE_COLORS = {
  Consulta: '#5B6B6E',
  Audiometría: '#3F9142',
  'Control audífono': '#B8790A',
  'Venta y calibración': '#2563A6',
  'Trabajo administrativo': '#8B5CF6',
  'Trabajo en terreno': '#8D6E63',
  Reunión: '#0EA5A5',
  'Receta de lentes': '#DB2777',
  Venta: '#0D9488',
};

export function apptTypesForModule(module) {
  return [...(module === 'ORL' ? ORL_APPT_TYPES : OFT_APPT_TYPES), ...BLOCK_TYPES];
}

export function isBlockType(type) {
  return BLOCK_TYPES.includes(type);
}

export async function listDayAppointments({ branchId, module, date }) {
  const { data, error } = await supabase
    .from('appointments')
    .select(
      'id, patient_id, date, time, type, duration, professional_id, notes, status, module, branch_id, booking_source, created_by, patients(nombre, apellido, segundo_nombre, segundo_apellido, phone, email, rut), professionals!professional_id(nombre, apellido, role), creator:professionals!created_by(nombre, apellido, role)'
    )
    .eq('branch_id', branchId)
    .eq('module', module)
    .eq('date', date)
    .is('deleted_at', null)
    .order('time', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// Para la vista mensual (spec 2.2, renderMonthGrid del prototipo): trae
// todas las citas del mes, livianas (sin embeds), agrupadas por día en el
// cliente.
export async function listMonthAppointments({ branchId, module, monthStart, monthEnd }) {
  const { data, error } = await supabase
    .from('appointments')
    .select('id, patient_id, date, time, type, patients(nombre, apellido, segundo_nombre, segundo_apellido)')
    .eq('branch_id', branchId)
    .eq('module', module)
    .gte('date', monthStart)
    .lt('date', monthEnd)
    .is('deleted_at', null)
    .order('time', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// Para el Dashboard (DECISIONS.md #44): trae solo `date`/`type`/`status`,
// liviano, para un rango arbitrario (los últimos 12 meses) — de ahí se
// derivan tanto el conteo del mes actual, la tendencia mensual y la
// distribución de estados del mes, sin repetir la consulta para cada cosa.
export async function listAppointmentsInRange({ branchId, module, startDate, endDate }) {
  const { data, error } = await supabase
    .from('appointments')
    .select('id, date, type, status')
    .eq('branch_id', branchId)
    .eq('module', module)
    .gte('date', startDate)
    .lte('date', endDate)
    .is('deleted_at', null);
  if (error) throw error;
  return data ?? [];
}

// Chequeo amigable ANTES de guardar (spec 2.3, findAppointmentConflict):
// trae todas las citas activas del mismo día/sucursal/módulo para que el
// cliente calcule superposiciones de horario. Para el caso "mismo
// profesional" la protección real es la restricción EXCLUDE de la base
// (DECISIONS.md #3) — esto es la capa de advertencia antes de llegar ahí.
export async function listPotentialConflicts({ branchId, module, date, excludeId }) {
  let query = supabase
    .from('appointments')
    .select('id, time, duration, type, professional_id, professionals!professional_id(nombre, apellido)')
    .eq('branch_id', branchId)
    .eq('module', module)
    .eq('date', date)
    .is('deleted_at', null);
  if (excludeId) query = query.neq('id', excludeId);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

function toMinutes(time) {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

// Superposición real de horario entre dos citas — y, si al menos una no
// tiene profesional asignado (bloqueo general), cualquier superposición
// del mismo módulo/sucursal cuenta como choque (spec 2.3).
export function findOverlaps(candidate, existing) {
  const start = toMinutes(candidate.time);
  const end = start + candidate.duration;
  return existing.filter((a) => {
    const aStart = toMinutes(a.time);
    const aEnd = aStart + a.duration;
    const overlaps = start < aEnd && aStart < end;
    if (!overlaps) return false;
    const involvesGeneralBlock = !candidate.professionalId || !a.professional_id;
    const samePro =
      candidate.professionalId && a.professional_id && candidate.professionalId === a.professional_id;
    return involvesGeneralBlock || samePro;
  });
}

export async function createAppointment(values, actorId) {
  const { error } = await supabase
    .from('appointments')
    .insert({ ...values, created_by: actorId, updated_by: actorId, booking_source: 'staff' });
  if (error) throw error;
}

export async function updateAppointment(id, values, actorId) {
  const { error } = await supabase
    .from('appointments')
    .update({ ...values, updated_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

export async function updateAppointmentStatus(id, status, actorId) {
  const { error } = await supabase
    .from('appointments')
    .update({ status, updated_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

// `reason` es opcional (solo se pide en la UI cuando hay paciente con
// correo, ver CancelAppointmentModal) — se guarda igual si viene, aunque
// después no haya correo que mandar, por si sirve como registro interno.
export async function softDeleteAppointment(id, actorId, reason) {
  const { error } = await supabase
    .from('appointments')
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: actorId,
      cancellation_reason: reason?.trim() || null,
    })
    .eq('id', id);
  if (error) throw error;
}

// Aviso por correo al paciente cuando se cancela una cita (pedido de
// Nicolás, 19-ago-2026) — se llama DESPUÉS de que softDeleteAppointment ya
// tuvo éxito, nunca antes: la cancelación de la cita no depende de que
// este correo se mande bien. "Mejor esfuerzo" a propósito (ver Edge
// Function notify-appointment-cancellation) — el resultado se guarda en la
// propia cita (`cancellation_email_status`) para poder revisar después
// cuáles fallaron.
export async function notifyAppointmentCancellation({
  appointmentId,
  patientId,
  reason,
  date,
  time,
  type,
  branchName,
  branchPhone,
}) {
  try {
    const { data, error } = await supabase.functions.invoke('notify-appointment-cancellation', {
      body: { appointmentId, patientId, reason, date, time, type, branchName, branchPhone },
    });
    if (error) return { ok: false, sent: false };
    return data ?? { ok: false, sent: false };
  } catch {
    // Silencioso a propósito — ver comentario arriba, esto nunca debe
    // afectar el flujo de cancelación, que ya terminó.
    return { ok: false, sent: false };
  }
}

// Arrastrar y soltar en la grilla del día (spec 2.2, onCalDrop del
// prototipo) — solo cambia la hora, mismo día.
export async function moveAppointmentTime(id, newTime, actorId) {
  const { error } = await supabase
    .from('appointments')
    .update({ time: newTime, updated_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

// Mensaje de recordatorio para WhatsApp (Agenda.jsx, handleWhatsApp) — se
// mantiene como link `wa.me` simple (no hay integración real con WhatsApp
// Business todavía, ver DECISIONS.md #76), pero desde el 20-ago-2026 trae
// la misma información que el correo de recordatorio: fecha completa (con
// año, mismo `formatDateLong` que usa el resto del software) y sucursal +
// dirección si se conocen. `branch` es opcional a propósito — si no se
// pasa, el mensaje sigue funcionando, solo sin esa línea.
export function reminderMessage(appt, patient, branch) {
  const fecha = formatDateLong(appt.date);
  const hora = appt.time?.slice(0, 5);
  const lugar = branch?.name ? ` en ${branch.name}${branch.address ? ` (${branch.address})` : ''}` : '';
  return `Hola ${patient.nombre}, te recordamos tu hora de ${appt.type} el ${fecha} a las ${hora} hrs${lugar}. Por favor llega 10 minutos antes. ¡Te esperamos! — Agenda Comunal, GlobalVision`;
}

// Aviso por correo REAL (Resend, ver Edge Function notify-appointment-reminder)
// al mandar un recordatorio manual desde Agenda (botón ✉️ → SendReminderModal).
// Reemplaza el `mailto:` que había antes (DECISIONS.md #76) — siempre a
// pedido explícito de un clic, nunca automático.
export async function notifyAppointmentReminder({
  appointmentId,
  patientId,
  date,
  time,
  type,
  branchName,
  branchAddress,
  branchPhone,
  branchMapsLinkEnabled,
}) {
  const { data, error } = await supabase.functions.invoke('notify-appointment-reminder', {
    body: { appointmentId, patientId, date, time, type, branchName, branchAddress, branchPhone, branchMapsLinkEnabled },
  });
  if (error) {
    const detail = await error.context?.json?.().catch(() => null);
    throw new Error(detail?.error || error.message || 'No se pudo mandar el recordatorio.');
  }
  return data;
}

// Solo deja registrado CUÁNDO se abrió el link de wa.me (Agenda.jsx,
// handleWhatsApp) — no hay forma de saber si esa persona realmente mandó
// el mensaje del otro lado, ver comentario de la columna en la migración
// 0033. Update directo, sin Edge Function: no hay ningún secreto de por
// medio, y `appointments_update` (RLS) ya permite esto con solo tener
// acceso a la sucursal de la cita.
export async function markWhatsappReminderOpened(appointmentId) {
  const { error } = await supabase
    .from('appointments')
    .update({ reminder_whatsapp_opened_at: new Date().toISOString() })
    .eq('id', appointmentId);
  if (error) throw error;
}
